import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconInfoCircle, IconPencil, IconPlus } from '@tabler/icons-react';
import { X, Zap } from 'lucide-react';
import {
  createCannedResponse,
  fetchCannedResponses,
  updateCannedResponse,
} from '../services/boardChatService';

function normalizeShortcut(value) {
  return String(value || '')
    .trim()
    .replace(/^\/+/, '')
    .trim()
    .slice(0, 50);
}

function shortcutLabel(shortcut) {
  const s = normalizeShortcut(shortcut);
  return s ? `/${s}` : '/';
}

export function BoardChatCannedResponsesMenu({
  onPick,
  onResponsesChange,
  onOpen,
  tipProps,
  hideTip,
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [shortcut, setShortcut] = useState('');
  const [content, setContent] = useState('');
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const wrapRef = useRef(null);
  const loadedRef = useRef(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchCannedResponses();
      const list = Array.isArray(res.data) ? res.data : [];
      setItems(list);
      onResponsesChange?.(list);
      loadedRef.current = true;
    } catch (e) {
      setError(e.message || 'Falha ao carregar respostas predefinidas');
      setItems([]);
      onResponsesChange?.([]);
    } finally {
      setLoading(false);
    }
  }, [onResponsesChange]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!open && !formOpen) return undefined;
    const onDoc = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (formOpen) {
        closeForm();
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', onDoc, true);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [formOpen, open]); // eslint-disable-line react-hooks/exhaustive-deps -- closeForm usa saving atual

  const handleToggle = () => {
    hideTip?.();
    setOpen((v) => {
      const next = !v;
      if (next) onOpen?.();
      return next;
    });
    if (!loadedRef.current) refresh();
  };

  const openForm = (item = null) => {
    hideTip?.();
    setOpen(false);
    setError('');
    setEditingItem(item);
    setShortcut(item?.shortcut || '');
    setContent(item?.content || '');
    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) return;
    setFormOpen(false);
    setEditingItem(null);
    setError('');
  };

  const handlePick = (item) => {
    setOpen(false);
    onPick?.(item.content || '');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const cleanShortcut = normalizeShortcut(shortcut);
    const cleanContent = content.trim();
    if (!cleanShortcut || !cleanContent) {
      setError('Preencha o atalho e a resposta.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (editingItem?.id) {
        await updateCannedResponse(editingItem.id, {
          shortcut: cleanShortcut,
          content: cleanContent,
        });
      } else {
        await createCannedResponse({
          shortcut: cleanShortcut,
          content: cleanContent,
        });
      }
      await refresh();
      setFormOpen(false);
      setEditingItem(null);
      setOpen(true);
    } catch (err) {
      setError(err.message || 'Falha ao salvar resposta predefinida');
    } finally {
      setSaving(false);
    }
  };

  const modal = formOpen && typeof document !== 'undefined' ? createPortal(
    <div className="bch-canned-modal-backdrop" role="presentation" onMouseDown={closeForm}>
      <form
        className="bch-canned-modal"
        aria-label={editingItem ? 'Editar resposta predefinida' : 'Adicionar resposta predefinida'}
        onSubmit={handleSave}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="bch-canned-modal-head">
          <div>
            <h3>{editingItem ? 'Editar resposta predefinida' : 'Nova resposta predefinida'}</h3>
            <p>Crie uma resposta rápida para usar durante o atendimento.</p>
          </div>
          <button
            type="button"
            className="bch-canned-modal-close"
            aria-label="Fechar"
            onClick={closeForm}
            disabled={saving}
          >
            <X size={16} strokeWidth={1.8} />
          </button>
        </div>

        <label className="bch-canned-field">
          <span>Resposta</span>
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Digite o texto que será inserido no campo de mensagem..."
            rows={5}
            autoFocus
          />
        </label>

        <label className="bch-canned-field">
          <span className="bch-canned-label-row">
            Atalho
            <span className="bch-canned-info" tabIndex={0} aria-label="Ajuda sobre atalho">
              <IconInfoCircle size={14} stroke={1.8} aria-hidden />
              <span className="bch-canned-info-tip" role="tooltip">
                <strong>Como usar:</strong>
                <span>
                  Digite este atalho com <b>/</b> na frente, por exemplo{' '}
                  <code>/bomdia</code>.
                </span>
              </span>
            </span>
          </span>
          <div className="bch-canned-shortcut-input">
            <span aria-hidden="true">/</span>
            <input
              type="text"
              value={shortcut}
              onChange={(e) => setShortcut(normalizeShortcut(e.target.value))}
              placeholder="bomdia"
              maxLength={50}
            />
          </div>
        </label>

        {error ? <p className="bch-canned-error">{error}</p> : null}

        <div className="bch-canned-modal-actions">
          <button type="button" className="bch-canned-secondary" onClick={closeForm}>
            Cancelar
          </button>
          <button type="submit" className="bch-canned-primary" disabled={saving}>
            {saving ? 'Salvando...' : editingItem ? 'Salvar alterações' : 'Salvar resposta'}
          </button>
        </div>
      </form>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <div className="bch-dd bch-canned-wrap" ref={wrapRef}>
        <button
          type="button"
          className={`bch-composer-tool bch-composer-tool--canned${open ? ' is-active' : ''}`}
          aria-label="Respostas predefinidas"
          aria-expanded={open}
          aria-haspopup="menu"
          {...(open || !tipProps ? {} : tipProps('Respostas predefinidas', 'above'))}
          onClick={handleToggle}
        >
          <Zap size={16} strokeWidth={1.8} />
        </button>
        {open ? (
          <div
            className="bch-dd-menu bch-dd-menu--up bch-canned-menu"
            role="menu"
            aria-label="Respostas predefinidas"
          >
            <button
              type="button"
              role="menuitem"
              className="bch-dd-item bch-canned-add"
              onClick={() => openForm()}
            >
              <IconPlus size={15} stroke={1.9} aria-hidden />
              <span className="bch-dd-item-label">Adicionar resposta pré-definida</span>
            </button>
            <span className="bch-canned-sep" aria-hidden="true" />
            {loading ? (
              <div className="bch-canned-empty">Carregando respostas...</div>
            ) : items.length ? (
              items.map((item) => (
                <div
                  key={item.id}
                  role="menuitem"
                  className="bch-canned-item-row"
                >
                  <button
                    type="button"
                    className="bch-canned-item-pick"
                    onClick={() => handlePick(item)}
                  >
                    <span className="bch-canned-item-main">
                      <span className="bch-canned-shortcut">{shortcutLabel(item.shortcut)}</span>
                      <span className="bch-canned-preview">{item.content}</span>
                    </span>
                  </button>
                  <button
                    type="button"
                    className="bch-canned-edit-btn"
                    aria-label={`Editar ${shortcutLabel(item.shortcut)}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      openForm(item);
                    }}
                  >
                    <IconPencil size={14} stroke={1.8} aria-hidden />
                  </button>
                </div>
              ))
            ) : (
              <div className="bch-canned-empty">Nenhuma resposta cadastrada</div>
            )}
          </div>
        ) : null}
      </div>

      {modal}
    </>
  );
}
